import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import {
  getEventShiftSelectionKey,
  getRelatedEventShiftSelectionKeys,
  isEventShiftSelected,
} from "./eventWeeklyLayout.constants";

/** @param {object|undefined} left @param {object|undefined} right */
const areSelectableEventMetasEqual = (left, right) =>
  Boolean(left) &&
  Boolean(right) &&
  left.id === right.id &&
  left.date === right.date &&
  left.isDraft === right.isDraft &&
  left.isShift === right.isShift &&
  left.publishAsEvent === right.publishAsEvent &&
  left.userId === right.userId &&
  Boolean(left.isUnassigned) === Boolean(right.isUnassigned);

/** @param {Record<string, object>} a @param {Record<string, object>} b */
const areSelectableEventMapsEqual = (a, b) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => areSelectableEventMetasEqual(a[key], b[key]));
};

/** @param {Record<string, object>} eventsMap */
const getSelectableKeysSignature = (eventsMap) =>
  Object.keys(eventsMap || {})
    .sort()
    .join("|");

/**
 * Bulk selection state for Events/Shifts calendar views.
 * Individual event/shift selections persist across Daily/Weekly/Monthly switches.
 * The Select All checkbox is scoped to the current visible set and resets when
 * that set changes (view switch, date navigation, layout change, etc.).
 * @param {{ t: Function }} options
 */
export function useEventsShiftsWeeklyBulkSelect({ t }) {
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState({});
  const [selectableEvents, setSelectableEvents] = useState({});
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  /**
   * When false, Select All stays unchecked even if every visible item is selected
   * (e.g. after switching views). Set true only by an explicit Select All check.
   */
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const selectableKeysSignatureRef = useRef("");

  const selectedKeys = useMemo(
    () => Object.keys(selectedEvents),
    [selectedEvents],
  );
  const hasSelection = selectedKeys.length > 0;

  const selectableKeys = useMemo(
    () => Object.keys(selectableEvents),
    [selectableEvents],
  );

  const isAllSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => {
      const meta = selectableEvents[key];
      if (!meta) return false;
      const calendarKey = getEventShiftSelectionKey(meta.id, meta.date);
      const isStaffScopedKey = key !== calendarKey;
      return isEventShiftSelected(
        selectedEvents,
        meta.id,
        meta.date,
        isStaffScopedKey ? meta.userId : undefined,
      );
    });

  // If any visible item is deselected, clear the Select All checkbox.
  useEffect(() => {
    if (!isAllSelected) {
      setSelectAllChecked(false);
    }
  }, [isAllSelected]);

  const handleBulkSelectModeChange = useCallback((enabled) => {
    setBulkSelectMode(enabled);
    if (!enabled) {
      setSelectedEvents({});
      setSelectAllChecked(false);
    }
  }, []);

  const handleSelectAllChange = useCallback(
    (checked) => {
      if (checked) {
        setSelectAllChecked(true);
        // Merge current-view items so selections from other views stay intact.
        setSelectedEvents((prev) => ({ ...prev, ...selectableEvents }));
        return;
      }
      setSelectAllChecked(false);
      // Deselect only what is visible in the current view (and related cross-view keys).
      setSelectedEvents((prev) => {
        const next = { ...prev };
        Object.values(selectableEvents).forEach((meta) => {
          if (meta?.id == null || !meta?.date) return;
          getRelatedEventShiftSelectionKeys(next, meta.id, meta.date).forEach(
            (relatedKey) => {
              delete next[relatedKey];
            },
          );
        });
        return next;
      });
    },
    [selectableEvents],
  );

  /**
   * Toggles selection for an event/shift card.
   * Clears related calendar + staff keys together so Daily/Weekly/Monthly stay in sync.
   */
  const handleEventSelectionToggle = useCallback((key, eventMeta) => {
    setSelectedEvents((prev) => {
      const next = { ...prev };
      const eventId = eventMeta?.id;
      const date = eventMeta?.date;

      if (eventId == null || !date) {
        if (next[key]) {
          delete next[key];
        } else if (eventMeta) {
          next[key] = eventMeta;
        }
        return next;
      }

      const calendarKey = getEventShiftSelectionKey(eventId, date);
      const relatedKeys = getRelatedEventShiftSelectionKeys(prev, eventId, date);
      const isStaffScopedKey = key !== calendarKey;
      const isCurrentlySelected = isStaffScopedKey
        ? Boolean(prev[key] || prev[calendarKey])
        : relatedKeys.length > 0;

      if (isCurrentlySelected) {
        if (isStaffScopedKey) {
          delete next[key];
          delete next[calendarKey];
        } else {
          relatedKeys.forEach((relatedKey) => {
            delete next[relatedKey];
          });
        }
        return next;
      }

      next[key] = eventMeta;
      return next;
    });
  }, []);

  /**
   * Updates the current view's selectable map (for Select All / checkbox scope).
   * Does not prune `selectedEvents` — selections must survive Daily/Weekly/Monthly
   * remounts, which often report an empty or narrower map while loading.
   * Resets the Select All checkbox whenever the visible key set changes.
   */
  const handleSelectableEventsUpdate = useCallback((eventsMap) => {
    const nextSignature = getSelectableKeysSignature(eventsMap);
    if (nextSignature !== selectableKeysSignatureRef.current) {
      selectableKeysSignatureRef.current = nextSignature;
      setSelectAllChecked(false);
    }

    setSelectableEvents((prev) =>
      areSelectableEventMapsEqual(prev, eventsMap) ? prev : eventsMap,
    );
    // Refresh metadata for overlapping keys only; never drop off-view selections.
    setSelectedEvents((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(prev).forEach((key) => {
        const updated = eventsMap[key];
        if (!updated || areSelectableEventMetasEqual(prev[key], updated)) {
          return;
        }
        next[key] = updated;
        changed = true;
      });
      return changed ? next : prev;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedEvents({});
    setSelectAllChecked(false);
  }, []);

  const showComingSoon = useCallback(
    (actionKey) => {
      toast(t("scheduler.actionComingSoon", { action: actionKey }));
    },
    [t],
  );

  return {
    bulkSelectMode,
    selectedEvents,
    hasSelection,
    isAllSelected,
    /** Toolbar Select All — does not persist across view/date/layout changes. */
    selectAllChecked: selectAllChecked && isAllSelected,
    isBulkActionLoading,
    setIsBulkActionLoading,
    handleBulkSelectModeChange,
    handleSelectAllChange,
    handleEventSelectionToggle,
    handleSelectableEventsUpdate,
    clearSelection,
    showComingSoon,
  };
}
